import React, { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import DSBox from "../../../system-design/DSBox";
import DSCard from "../../../system-design/DSCard";
import DSCardContent from "../../../system-design/DSCardContent";
import DSSectionHeading from "../../../system-design/DSSectionHeading";
import { sectionCardSx } from "../../../system-design/sectionCardSx";
import DSTypography from "../../../system-design/DSTypography";
import DSSelect from "../../../system-design/DSSelect";
import DSMenuItem from "../../../system-design/DSMenuItem";
import DSFormControl from "../../../system-design/DSFormControl";
import DSInputLabel from "../../../system-design/DSInputLabel";
import DSSecondaryButton from "../../../system-design/DSSecondaryButton";
import AudioPlayer from "../../../../components/AudioPlayer/AudioPlayer";
import { useDevice } from "../../context/DeviceModalContext";
import collectionApiClient from "../../../../api/collection";
import { getChimeDisplayName } from "../../../../utils/chimeUtils";
import { Klydo } from "../../../../api/klydo";

const DeviceDefaultChimeCard = () => {
  const { device, updateConfig } = useDevice();

  const collectionIds = useMemo(
    () => (device.machineCollections?.value || []).map((c) => c.id),
    [device.machineCollections],
  );

  const collectionQueries = useQueries({
    queries: collectionIds.map((id) => ({
      queryKey: ["collection", id],
      queryFn: () => collectionApiClient.getCollectionById(id),
    })),
  });

  const klydosWithChimes = useMemo(() => {
    const seen = new Set<string>();
    const result: Klydo[] = [];
    for (const q of collectionQueries) {
      for (const klydo of q.data?.klydos || []) {
        if (
          !seen.has(klydo.id) &&
          klydo.chimesUrls &&
          klydo.chimesUrls.length > 0
        ) {
          seen.add(klydo.id);
          result.push(klydo);
        }
      }
    }
    return result;
  }, [collectionQueries]);

  const currentId = device.defaultCustomChimeId?.value ?? "";
  const selectedKlydo = klydosWithChimes.find((k) => k.id === currentId);

  const isLoading = collectionQueries.some((q) => q.isLoading);

  return (
    <DSBox sx={{ mb: 2 }}>
      <DSCard sx={sectionCardSx}>
        <DSSectionHeading>Default Chime</DSSectionHeading>
        <DSCardContent>
          {isLoading ? (
            <DSTypography variant="body2">Loading chimes...</DSTypography>
          ) : klydosWithChimes.length === 0 ? (
            <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
              No klydos with chimes in this device&apos;s collections
            </DSTypography>
          ) : (
            <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <DSFormControl fullWidth size="small">
                <DSInputLabel id="default-chime-label">
                  Chime klydo
                </DSInputLabel>
                <DSSelect
                  labelId="default-chime-label"
                  label="Chime klydo"
                  value={currentId}
                  onChange={(e) =>
                    updateConfig(
                      "defaultCustomChimeId",
                      e.target.value as string,
                    )
                  }
                >
                  {klydosWithChimes.map((klydo) => (
                    <DSMenuItem key={klydo.id} value={klydo.id}>
                      {klydo.name || klydo.id}
                    </DSMenuItem>
                  ))}
                </DSSelect>
              </DSFormControl>

              {selectedKlydo?.chimesUrls?.[0] && (
                <AudioPlayer
                  src={selectedKlydo.chimesUrls[0]}
                  title={getChimeDisplayName(
                    selectedKlydo.chimesUrls[0],
                    0,
                    selectedKlydo.metadata?.chimesFileName,
                  )}
                />
              )}

              {currentId && (
                <DSSecondaryButton
                  onClick={() => updateConfig("defaultCustomChimeId", "")}
                  sx={{ alignSelf: "flex-start" }}
                >
                  Reset to Default
                </DSSecondaryButton>
              )}
            </DSBox>
          )}
        </DSCardContent>
      </DSCard>
    </DSBox>
  );
};

export default DeviceDefaultChimeCard;
